import { ACCOUNT_ELIGIBILITY_POLICY } from "../config/account-eligibility-policy.js";
import { IDENTITY_UNIQUENESS_POLICY } from "../config/identity-uniqueness-policy.js";
import {
  REGISTRATION_ACTIVATION_GATES,
  REGISTRATION_STATE,
  isRegistrationActivationReady,
} from "../config/registration-state.js";
import { TERMS_CONTENT } from "../config/terms-content.js";

export const REGISTRATION_READINESS_CONTRACT = Object.freeze({
  id: "exiled-systems-registration-readiness",
  version: "2026-07-22.1",
});

const PUBLIC_PRODUCTION_HOSTS = Object.freeze(new Set([
  "exiledsystems.com",
  "www.exiledsystems.com",
]));
const SHA256_PATTERN = /^[0-9a-f]{64}$/u;
const SENSITIVE_OPTION_PATTERN = /(?:authorization|api[-_]?key|client[-_]?secret|password|private[-_]?key|token)/iu;
const MAX_CONFIGURATION_BYTES = 128 * 1024;
const MAX_SUBMISSION_BYTES = 64 * 1024;

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  for (const nested of Object.values(value)) deepFreeze(nested);
  return value;
}

function lockedRegistrationState() {
  return deepFreeze({ ...REGISTRATION_STATE });
}

function lockedSnapshot(reason, detail = null) {
  return deepFreeze({
    ready: false,
    status: "locked",
    reason,
    detail,
    registration: lockedRegistrationState(),
    policies: null,
  });
}

function exactStringSet(candidate, expected) {
  return Array.isArray(candidate)
    && candidate.length === expected.length
    && new Set(candidate).size === candidate.length
    && candidate.every((value) => expected.includes(value));
}

function policyIdentityMatches(candidate, expected) {
  return Boolean(
    candidate
    && candidate.policyId === expected.policyId
    && candidate.version === expected.version
    && candidate.effective === true
  );
}

function identityPolicyMatches(candidate) {
  const candidateIdentifiers = candidate?.identifiers;
  const expectedIdentifiers = IDENTITY_UNIQUENESS_POLICY.identifiers;
  return Boolean(
    policyIdentityMatches(candidate, IDENTITY_UNIQUENESS_POLICY)
    && candidate.serverAuthorityRequired === IDENTITY_UNIQUENESS_POLICY.serverAuthorityRequired
    && candidate.failClosed === true
    && Array.isArray(candidateIdentifiers)
    && candidateIdentifiers.length === expectedIdentifiers.length
    && candidateIdentifiers.every((identifier, index) => (
      identifier?.fieldId === expectedIdentifiers[index].fieldId
      && identifier?.kind === expectedIdentifiers[index].kind
      && identifier?.normalization === expectedIdentifiers[index].normalization
      && identifier?.verifiedReuse === expectedIdentifiers[index].verifiedReuse
    ))
  );
}

function eligibilityPolicyMatches(candidate) {
  return Boolean(
    policyIdentityMatches(candidate, ACCOUNT_ELIGIBILITY_POLICY)
    && candidate.serverAuthorityRequired === ACCOUNT_ELIGIBILITY_POLICY.serverAuthorityRequired
    && candidate.age?.minimumAge === ACCOUNT_ELIGIBILITY_POLICY.age.minimumAge
    && candidate.age?.maximumAgeExclusive === ACCOUNT_ELIGIBILITY_POLICY.age.maximumAgeExclusive
    && exactStringSet(candidate.email?.allowedDomains, ACCOUNT_ELIGIBILITY_POLICY.email.allowedDomains)
    && exactStringSet(candidate.publicIdentity?.reservedBrandTokens, ACCOUNT_ELIGIBILITY_POLICY.publicIdentity.reservedBrandTokens)
  );
}

function policyReceipt(candidate) {
  if (
    !candidate
    || candidate.effective !== true
    || typeof candidate.version !== "string"
    || !candidate.version
    || !SHA256_PATTERN.test(candidate.contentSha256 || "")
  ) return null;
  return Object.freeze({
    version: candidate.version,
    contentSha256: candidate.contentSha256,
  });
}

function termsPolicyMatches(candidate, terms) {
  return Boolean(
    terms?.effective === true
    && candidate?.effective === true
    && candidate.policyId === terms.policyId
    && candidate.version === terms.version
    && candidate.contentRevision === terms.contentRevision
    && candidate.effectiveDate === terms.effectiveDate
    && candidate.locale === terms.locale
    && policyReceipt(candidate)
  );
}

function exactConsentMatches(candidate, terms) {
  return Boolean(
    candidate?.accepted === true
    && candidate.policyId === terms.policyId
    && candidate.version === terms.version
    && candidate.contentRevision === terms.contentRevision
    && candidate.effectiveDate === terms.effectiveDate
    && candidate.locale === terms.locale
  );
}

function isLoopbackHostname(hostname) {
  const normalized = String(hostname || "").toLowerCase();
  return normalized === "localhost" || normalized === "127.0.0.1" || normalized === "[::1]";
}

function parseServiceBaseUrl(apiBaseUrl, browserOrigin) {
  if (typeof apiBaseUrl !== "string" || !apiBaseUrl.trim()) {
    throw new TypeError("An account-service base URL is required.");
  }
  const origin = new URL(browserOrigin || "http://127.0.0.1");
  const target = new URL(apiBaseUrl, origin);
  if (!new Set(["http:", "https:"]).has(target.protocol)) {
    throw new TypeError("The account-service URL must use HTTP or HTTPS.");
  }
  if (target.username || target.password || target.search || target.hash) {
    throw new TypeError("The account-service URL cannot contain credentials, a query, or a fragment.");
  }
  if (target.protocol !== "https:" && !isLoopbackHostname(target.hostname)) {
    throw new TypeError("Remote account-service URLs must use HTTPS.");
  }
  if (target.origin !== origin.origin && !(isLoopbackHostname(target.hostname) && isLoopbackHostname(origin.hostname))) {
    throw new TypeError("The signup adapter is same-origin except for loopback development.");
  }
  target.pathname = `${target.pathname.replace(/\/+$/u, "")}/`;
  return target;
}

function assertNoClientSecrets(options) {
  const forbidden = Object.keys(options || {}).filter((key) => SENSITIVE_OPTION_PATTERN.test(key));
  if (forbidden.length) {
    throw new TypeError("Client-side registration adapters cannot receive credentials or secrets.");
  }
}

function registrationStateFromServer(configuration) {
  const gates = configuration?.gates;
  if (!gates || typeof gates !== "object") return null;
  const registration = { ...REGISTRATION_STATE, status: "open", statusLabel: "REGISTRATION OPEN" };
  for (const { key } of REGISTRATION_ACTIVATION_GATES) {
    if (gates[key] !== true) return null;
    registration[key] = true;
  }
  registration.publicMessage = "Registration is connected to the server-authoritative Exiled Systems account service.";
  return deepFreeze(registration);
}

function evaluateConfiguration(configuration, terms, expectedEnvironment) {
  if (
    configuration?.contract?.id !== REGISTRATION_READINESS_CONTRACT.id
    || configuration?.contract?.version !== REGISTRATION_READINESS_CONTRACT.version
  ) return lockedSnapshot("contract-missing-or-stale");
  if (configuration.environment !== expectedEnvironment) return lockedSnapshot("server-environment-mismatch");
  if (configuration.enabled !== true || configuration.status !== "open") {
    return lockedSnapshot("server-registration-locked");
  }
  if (
    configuration.minimumAge !== ACCOUNT_ELIGIBILITY_POLICY.age.minimumAge
    || configuration.maximumAgeExclusive !== ACCOUNT_ELIGIBILITY_POLICY.age.maximumAgeExclusive
    || !exactStringSet(configuration.acceptedEmailDomains, ACCOUNT_ELIGIBILITY_POLICY.email.allowedDomains)
  ) return lockedSnapshot("server-eligibility-mismatch");

  const identityPolicy = configuration.policies?.identityUniqueness;
  const eligibilityPolicy = configuration.policies?.accountEligibility;
  if (!identityPolicyMatches(identityPolicy) || !eligibilityPolicyMatches(eligibilityPolicy)) {
    return lockedSnapshot("server-policy-mismatch");
  }
  if (!termsPolicyMatches(configuration.policies?.terms, terms)) {
    return lockedSnapshot("terms-not-effective-or-mismatched");
  }
  const termsReceipt = policyReceipt(configuration.policies?.terms);
  const privacyReceipt = policyReceipt(configuration.policies?.privacy);
  if (!termsReceipt || !privacyReceipt) return lockedSnapshot("legal-policy-receipt-unavailable");

  const registration = registrationStateFromServer(configuration);
  if (!registration || !isRegistrationActivationReady(registration, identityPolicy, eligibilityPolicy)) {
    return lockedSnapshot("required-server-gate-missing");
  }
  return deepFreeze({
    ready: true,
    status: "open",
    reason: null,
    detail: null,
    registration,
    policies: { terms: termsReceipt, privacy: privacyReceipt },
  });
}

async function readJsonResponse(response, maximumBytes) {
  const contentType = response?.headers?.get?.("content-type") || "";
  if (!/application\/json/iu.test(contentType)) throw new Error("non-json-response");
  const text = await response.text();
  if (text.length > maximumBytes) throw new Error("response-too-large");
  return JSON.parse(text);
}

function timeoutSignal(timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return { signal: controller.signal, stop: () => clearTimeout(timer) };
}

export class RegistrationAdapterError extends Error {
  constructor(code, message, { retryable = false } = {}) {
    super(message);
    this.name = "RegistrationAdapterError";
    this.code = code;
    this.retryable = retryable;
  }
}

export function createGhostlineAccountServiceAdapter(options = {}) {
  assertNoClientSecrets(options);
  const {
    activation = "disabled",
    deploymentTier = "production",
    apiBaseUrl = "",
    browserOrigin = globalThis.location?.origin,
    fetchImpl = globalThis.fetch,
    terms = TERMS_CONTENT,
    timeoutMs = 5_000,
  } = options;

  if (!new Set(["disabled", "staging", "production"]).has(activation)) {
    throw new TypeError("Registration adapter activation must be disabled, staging, or production.");
  }
  if (deploymentTier !== "production" && deploymentTier !== "staging" && deploymentTier !== "test") {
    throw new TypeError("Deployment tier must be production, staging, or test.");
  }
  if (!Number.isInteger(timeoutMs) || timeoutMs < 250 || timeoutMs > 15_000) {
    throw new TypeError("Registration adapter timeout must be 250 through 15000 milliseconds.");
  }
  if (typeof fetchImpl !== "function") throw new TypeError("A fetch implementation is required.");

  const runtimeHost = browserOrigin ? new URL(browserOrigin).hostname.toLowerCase() : "";
  const runtimeProtocol = browserOrigin ? new URL(browserOrigin).protocol : "";
  const stagingEnabled = activation === "staging"
    && (deploymentTier === "staging" || deploymentTier === "test")
    && !PUBLIC_PRODUCTION_HOSTS.has(runtimeHost);
  const productionEnabled = activation === "production"
    && deploymentTier === "production"
    && PUBLIC_PRODUCTION_HOSTS.has(runtimeHost)
    && runtimeProtocol === "https:";
  const runtimeEnabled = stagingEnabled || productionEnabled;
  const expectedEnvironment = productionEnabled ? "production" : "staging";
  const baseUrl = runtimeEnabled ? parseServiceBaseUrl(apiBaseUrl, browserOrigin) : null;
  let snapshot = runtimeEnabled
    ? lockedSnapshot("configuration-not-fetched")
    : lockedSnapshot("adapter-disabled-for-runtime");

  function endpoint(pathname) {
    return new URL(pathname.replace(/^\/+/, ""), baseUrl);
  }

  async function refresh() {
    if (!runtimeEnabled) return snapshot;
    const timeout = timeoutSignal(timeoutMs);
    try {
      const response = await fetchImpl(endpoint("v1/config/registration"), {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        referrerPolicy: "no-referrer",
        signal: timeout.signal,
      });
      if (!response.ok) {
        snapshot = lockedSnapshot("configuration-request-failed", `http-${response.status}`);
        return snapshot;
      }
      const configuration = await readJsonResponse(response, MAX_CONFIGURATION_BYTES);
      snapshot = evaluateConfiguration(configuration, terms, expectedEnvironment);
      return snapshot;
    } catch (error) {
      snapshot = lockedSnapshot(
        error?.name === "AbortError" ? "configuration-timeout" : "configuration-unavailable",
      );
      return snapshot;
    } finally {
      timeout.stop();
    }
  }

  async function submit(request) {
    const current = await refresh();
    if (!current.ready) {
      throw new RegistrationAdapterError(
        "REGISTRATION_LOCKED",
        "Registration is not available yet.",
        { retryable: current.reason === "configuration-unavailable" || current.reason === "configuration-timeout" },
      );
    }
    if (!request?.account || !exactConsentMatches(request.consent, terms)) {
      throw new RegistrationAdapterError(
        "POLICY_REVIEW_REQUIRED",
        "Review the current policies before continuing.",
      );
    }
    const body = JSON.stringify({
      account: request.account,
      consent: {
        accepted: true,
        terms: current.policies.terms,
        privacy: current.policies.privacy,
      },
    });
    if (body.length > MAX_SUBMISSION_BYTES) {
      throw new RegistrationAdapterError("REQUEST_TOO_LARGE", "The account request is too large.");
    }

    const timeout = timeoutSignal(timeoutMs);
    try {
      const response = await fetchImpl(endpoint("v1/registrations"), {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        referrerPolicy: "no-referrer",
        signal: timeout.signal,
        body,
      });
      const result = await readJsonResponse(response, MAX_CONFIGURATION_BYTES).catch(() => null);
      if (
        response.status !== 202
        || result?.status !== "awaiting_verification"
        || typeof result?.message !== "string"
      ) {
        throw new RegistrationAdapterError(
          result?.code === "POLICY_REVIEW_REQUIRED" ? "POLICY_REVIEW_REQUIRED" : "REGISTRATION_REJECTED",
          result?.code === "POLICY_REVIEW_REQUIRED"
            ? "The policies changed. Review them before continuing."
            : "The account request could not be accepted.",
          { retryable: response.status >= 500 },
        );
      }
      return Object.freeze({
        status: "awaiting_verification",
        message: result.message,
        cooldownSeconds: Number.isInteger(result.cooldownSeconds) ? result.cooldownSeconds : null,
      });
    } catch (error) {
      if (error instanceof RegistrationAdapterError) throw error;
      throw new RegistrationAdapterError(
        error?.name === "AbortError" ? "REGISTRATION_TIMEOUT" : "REGISTRATION_UNAVAILABLE",
        "The account request could not be completed right now.",
        { retryable: true },
      );
    } finally {
      timeout.stop();
    }
  }

  return Object.freeze({
    refresh,
    submit,
    getState: () => snapshot,
  });
}

